import Phaser from "phaser";
import {
  BACKGROUNDS,
  BACKGROUND_DEFAULT,
  BackgroundName,
} from "../assets/backgrounds";
import { SHOW_DEBUG } from "../config";

type Anchor = "left" | "right" | "top" | "bottom" | "center";

// Desired Y position for the cutoff after scaling (in screen pixels)
export const TOOLBAR_HEIGHT = 75;
export const BOTTOM_NAVIGATION_HEIGHT = 0; // TODO: re-enable this 56;

/**
 * Simple helper that draws an unscaled background image (top-left anchored)
 * and a red guide line that marks the usable area cutoff.
 */
export class BackgroundImage {
  readonly image: Phaser.GameObjects.Image;
  readonly guide: Phaser.GameObjects.Line;
  readonly cutoffY: number;

  // ---------------- Static helpers -----------------------------
  private static bgOrder: BackgroundName[] = [];
  private static pointer = 0;

  /**
   * Returns the next background name in a shuffled cycle. After all
   * backgrounds have been used once, the list reshuffles and repeats.
   */
  public static getNextBackground(): BackgroundName {
    // Regenerate and shuffle order if first call or cycle completed
    if (
      BackgroundImage.bgOrder.length === 0 ||
      BackgroundImage.pointer >= BackgroundImage.bgOrder.length
    ) {
      BackgroundImage.bgOrder = Object.keys(BACKGROUNDS) as BackgroundName[];

      // Fisher–Yates shuffle
      for (let i = BackgroundImage.bgOrder.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [BackgroundImage.bgOrder[i], BackgroundImage.bgOrder[j]] = [
          BackgroundImage.bgOrder[j],
          BackgroundImage.bgOrder[i],
        ];
      }

      // Ensure the default background is first for a fast initial load
      const defIdx = BackgroundImage.bgOrder.indexOf(BACKGROUND_DEFAULT);
      if (defIdx > 0) {
        [BackgroundImage.bgOrder[0], BackgroundImage.bgOrder[defIdx]] = [
          BackgroundImage.bgOrder[defIdx],
          BackgroundImage.bgOrder[0],
        ];
      }

      BackgroundImage.pointer = 0;
    }

    const name = BackgroundImage.bgOrder[BackgroundImage.pointer];
    BackgroundImage.pointer += 1;
    return name;
  }

  constructor(scene: Phaser.Scene, bgName: BackgroundName) {
    const cfg = BACKGROUNDS[bgName];
    const textureKey = "bg_" + bgName;
    const anchor = cfg.anchor as Anchor;
    const originalCutoff = cfg.cutoff; // pixel row in source image to align

    // Grab viewport size
    const viewW = scene.scale.width;
    const viewH = scene.scale.height;

    // Obtain source image dimensions
    const tex = scene.textures
      .get(textureKey)
      .getSourceImage() as HTMLImageElement;
    const imgW = tex.width;
    const imgH = tex.height;

    // Calculate minimum scale factors to ensure:
    // 1) Horizontal coverage
    // 2) The area above and below the cutoff fully cover the viewport when the cutoff is fixed at TARGET_CUTOFF_Y
    const minScaleW = viewW / imgW;
    const minScaleTop = TOOLBAR_HEIGHT / originalCutoff;
    const minScaleBottom = (viewH - TOOLBAR_HEIGHT) / (imgH - originalCutoff);

    // Choose the largest to satisfy all constraints (cover behavior retained)
    const scale = Math.max(minScaleW, minScaleTop, minScaleBottom);

    // Center the image inside the viewport
    const displayW = imgW * scale;
    const displayH = imgH * scale;
    let offsetX = (viewW - displayW) / 2;

    // Offset Y so that the scaled cutoff row aligns exactly with TARGET_CUTOFF_Y
    let offsetY = TOOLBAR_HEIGHT - originalCutoff * scale;

    if (anchor === "left") offsetX = 0;
    if (anchor === "right") offsetX = viewW - displayW;

    this.image = scene.add.image(offsetX, offsetY, textureKey).setOrigin(0, 0);
    this.image.setScale(scale);
    this.image.setDepth(-1000);

    // Position red guide at the target cutoff Y
    this.cutoffY = TOOLBAR_HEIGHT;

    // Draw guide line across the entire viewport width; only visible when debugging
    this.guide = scene.add
      .line(0, 0, 0, TOOLBAR_HEIGHT, viewW, TOOLBAR_HEIGHT, 0xff0000)
      .setOrigin(0, 0)
      .setLineWidth(2)
      .setDepth(-900)
      .setVisible(SHOW_DEBUG);
  }
}
